// LMS data-access layer: Supabase first, local marketing copy fallback.
// Ported from CreatINN lib/db-adapter.ts, rewritten for Supabase JS + LSEM courses.ts.
import { supabase } from '../integrations/supabase/client';
import { allCourses, courseDetails } from '../data/courses';
import { flagshipAsLms, allFlagshipAsLms } from '../data/flagship';
import type { LmsCourse, LmsModule } from '../types/lms';

function localCourseAsLms(id: string): LmsCourse | null {
  const c = allCourses.find((x) => x.id === id);
  if (!c) return null;
  const d = courseDetails[id];
  const modules: LmsModule[] = (d?.modules ?? []).map((m, mi) => ({
    id: `local-${id}-m${mi}`,
    course_id: id,
    title: m.title,
    description: '',
    position: mi,
    lessons: m.topics.map((t, li) => ({
      id: `local-${id}-m${mi}-l${li}`,
      module_id: `local-${id}-m${mi}`,
      title: t,
      content: '',
      video_url: null,
      duration: '',
      position: li,
      is_free: li === 0,
      resources: '',
    })),
  }));
  return {
    id: c.id,
    slug: c.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    title: c.title,
    description: d?.overview ?? c.description,
    tagline: d?.tagline ?? '',
    thumbnail: c.image,
    price_value: d?.priceValue ?? 0,
    duration: c.duration,
    level: c.level,
    category: c.category,
    is_published: true,
    is_featured: true,
    modules,
  };
}

export async function getCourseWithContent(courseId: string): Promise<LmsCourse | null> {
  try {
    const { data: course, error } = await supabase.from('courses').select('*').eq('id', courseId).single();
    if (error || !course) return localCourseAsLms(courseId) ?? flagshipAsLms(courseId);
    const c = course as unknown as Omit<LmsCourse, 'modules'>;
    const { data: modules } = await supabase
      .from('modules')
      .select('*')
      .eq('course_id', courseId)
      .order('position');
    const mods = ((modules ?? []) as unknown as Omit<LmsModule, 'lessons'>[]).map((m) => ({ ...m, lessons: [] as LmsModule['lessons'] }));
    if (mods.length === 0) {
      // No LMS content seeded yet -> show marketing modules as lesson placeholders
      return { ...(c as LmsCourse), modules: localCourseAsLms(courseId)?.modules ?? [] };
    }
    for (const m of mods) {
      const { data: lessons } = await supabase.from('lessons').select('*').eq('module_id', m.id).order('position');
      m.lessons = ((lessons ?? []) as unknown as LmsModule['lessons']);
    }
    return { ...(c as LmsCourse), modules: mods };
  } catch {
    return localCourseAsLms(courseId) ?? flagshipAsLms(courseId);
  }
}

export async function getEnrollment(userId: string, courseId: string) {
  const { data } = await supabase
    .from('enrollments')
    .select('*')
    .eq('user_id', userId)
    .eq('course_id', courseId)
    .maybeSingle();
  return data;
}

export async function getCompletedLessonIds(userId: string, lessonIds: string[]): Promise<Set<string>> {
  if (lessonIds.length === 0) return new Set();
  // Local placeholder ids are never in DB
  const dbIds = lessonIds.filter((id) => !id.startsWith('local-'));
  if (dbIds.length === 0) {
    // fall back to localStorage for pre-seed content
    try {
      const raw = localStorage.getItem(`lsem-progress-${userId}`);
      return new Set((JSON.parse(raw || '[]') as string[]).filter((id) => lessonIds.includes(id)));
    } catch {
      return new Set();
    }
  }
  const { data } = await supabase.from('progress').select('lesson_id').eq('user_id', userId).in('lesson_id', dbIds);
  const done = new Set(((data ?? []) as unknown as { lesson_id: string }[]).map((p) => p.lesson_id));
  try {
    const raw = localStorage.getItem(`lsem-progress-${userId}`);
    (JSON.parse(raw || '[]') as string[]).forEach((id) => {
      if (lessonIds.includes(id)) done.add(id);
    });
  } catch { /* ignore */ }
  return done;
}

export async function markLessonComplete(userId: string, lessonId: string, completed: boolean) {
  if (lessonId.startsWith('local-')) {
    // Pre-seed content: track locally until real lessons exist
    try {
      const key = `lsem-progress-${userId}`;
      const set = new Set(JSON.parse(localStorage.getItem(key) || '[]') as string[]);
      if (completed) set.add(lessonId);
      else set.delete(lessonId);
      localStorage.setItem(key, JSON.stringify([...set]));
    } catch { /* ignore */ }
    return;
  }
  if (completed) {
    await supabase.from('progress').upsert({ user_id: userId, lesson_id: lessonId, completed: true }, { onConflict: 'user_id,lesson_id' });
  } else {
    await supabase.from('progress').delete().eq('user_id', userId).eq('lesson_id', lessonId);
  }
}

export async function enrollInCourse(userId: string, courseId: string, paystackRef?: string) {
  const { data, error } = await supabase
    .from('enrollments')
    .upsert({ user_id: userId, course_id: courseId, status: 'active', paystack_ref: paystackRef ?? null }, { onConflict: 'user_id,course_id' })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export interface EnrollmentWithCourse {
  course_id: string;
  status: string;
  progress: number;
  paystack_ref: string | null;
  enrolled_at: string;
  course: LmsCourse | null;
}

export async function getMyEnrollments(userId: string): Promise<EnrollmentWithCourse[]> {
  const { data, error } = await supabase.from('enrollments').select('*').eq('user_id', userId).order('enrolled_at', { ascending: false });
  if (error) return [];
  const rows = (data ?? []) as unknown as { course_id: string; status: string; progress: number; paystack_ref: string | null; enrolled_at: string }[];
  const out: EnrollmentWithCourse[] = [];
  for (const r of rows) {
    const course = await getCourseWithContent(r.course_id).catch(() => null);
    out.push({ ...r, course });
  }
  return out;
}

export interface PaymentRow {
  id: string;
  course_id: string | null;
  amount: number;
  status: string;
  created_at: string;
}

export async function getMyPayments(userId: string): Promise<PaymentRow[]> {
  const { data, error } = await supabase.from('payments').select('*').eq('user_id', userId).order('created_at', { ascending: false });
  if (error) return [];
  return (data ?? []) as unknown as PaymentRow[];
}

export async function getPublishedCourses(): Promise<LmsCourse[]> {
  const fallback = () => [
    ...allCourses.map((c) => localCourseAsLms(c.id)!).filter(Boolean),
    ...allFlagshipAsLms(),
  ];
  try {
    const { data, error } = await supabase.from('courses').select('*').eq('is_published', true).order('price_value');
    if (error || !data || data.length === 0) return fallback();
    const rows = data as unknown as Omit<LmsCourse, 'modules'>[];
    return rows.map((c) => ({ ...c, modules: localCourseAsLms(c.id)?.modules ?? flagshipAsLms(c.id)?.modules ?? [] }));
  } catch {
    return fallback();
  }
}

/** Homepage featured cards. Newest admin-created programme wins, then older ones. */
export async function getFeaturedCourses(limit = 3): Promise<LmsCourse[]> {
  const courses = await getPublishedCourses();
  return courses
    .filter((course) => course.is_featured !== false)
    .sort((a, b) => {
      const aTime = a.created_at ? Date.parse(a.created_at) : 0;
      const bTime = b.created_at ? Date.parse(b.created_at) : 0;
      return bTime - aTime;
    })
    .slice(0, limit);
}
